import React from 'react';

/**
 * PanelHeader - Merkezi Panel Başlığı Bileşeni
 * 
 * Tüm sayfalarda tutarlı panel başlıkları için kullanılır.
 * Otomatik olarak tema sisteminden renkleri alır.
 * 
 * @example
 * <PanelHeader title="Seçilen Konular" />
 * 
 * @example
 * <PanelHeader 
 *   title="Risk Detayları" 
 *   subtitle="Risk ID: 123"
 *   icon={<AlertCircle size={20} />}
 *   actions={<button>Kaydet</button>}
 * />
 */

export interface PanelHeaderProps {
  /** Ana başlık metni */
  title: string;
  
  /** Alt başlık (opsiyonel) */
  subtitle?: string;
  
  /** Başlık yanında gösterilecek ikon (opsiyonel) */
  icon?: React.ReactNode;
  
  /** Sağ tarafta gösterilecek aksiyonlar (opsiyonel) */
  actions?: React.ReactNode;
  
  /** Ek CSS class'ları */
  className?: string;
  
  /** Sticky pozisyon (varsayılan: true) */
  sticky?: boolean;
  
  /** Stil varyantı */
  variant?: 'default' | 'compact' | 'large';
  
  /** Metin hizalama */
  align?: 'left' | 'center' | 'right';
}

const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
  className = '',
  sticky = true,
  variant = 'default',
  align = 'center'
}) => {
  // Variant'a göre padding ve font boyutları
  const variantStyles = {
    default: {
      padding: 'var(--section-header-padding-y) var(--section-header-padding-x)',
      fontSize: 'var(--section-header-title-size)'
    },
    compact: {
      padding: 'calc(var(--section-header-padding-y) - 0.03rem) var(--section-header-padding-x)',
      fontSize: 'calc(var(--section-header-title-size) - 0.03rem)'
    },
    large: {
      padding: 'var(--page-header-padding-y) var(--page-header-padding-x)',
      fontSize: 'var(--page-header-title-size)'
    }
  };

  const currentVariant = variantStyles[variant];
  const headerPadding = currentVariant.padding;
  const headerMinHeight = variant === 'default'
    ? 'var(--section-header-min-height)'
    : variant === 'compact'
      ? 'var(--section-header-min-height)'
      : 'var(--page-header-min-height)';

  // Hizalama stilleri
  const alignmentStyles = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end'
  };

  const sectionClassName = variant === 'compact'
    ? 'section-theme-header section-theme-header--compact'
    : 'section-theme-header';

  const compactClassName = variant === 'compact'
    ? 'panel-header--compact section-theme-header--compact'
    : '';

  return (
    <div
      className={`panel-header ${sectionClassName} ${compactClassName} ${className}`}
      style={{
        padding: headerPadding,
        minHeight: headerMinHeight,
        position: sticky ? 'sticky' : 'relative',
        top: sticky ? 0 : 'auto',
        zIndex: sticky ? 5 : 'auto',
        display: 'flex',
        justifyContent: actions ? 'space-between' : alignmentStyles[align],
        alignItems: 'center',
        gap: '0.75rem',
        borderRadius: 'var(--panel-header-radius) var(--panel-header-radius) 0 0',
        marginBottom: 0
      }}
    >
      {/* Sol Taraf: İkon + Başlık */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flex: actions ? 1 : 'none',
          minWidth: 0
        }}
      >
        {icon && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            {icon}
          </span>
        )}
        
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            minWidth: 0,
            flex: 1
          }}
        >
          <h2
            className="panel-title"
            style={{
              fontSize: currentVariant.fontSize,
              fontWeight: 'bold',
              margin: 0,
              textAlign: align,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {title}
          </h2>
          
          {subtitle && (
            <p
              style={{
                fontSize: '0.75rem',
                margin: 0,
                opacity: 0.9,
                textAlign: align,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Sağ Taraf: Aksiyonlar */}
      {actions && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0
          }}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

export default PanelHeader;
